import { loadImageFromURL } from '@/scenes/engine/utils/image-loader'

export const BATCH_BADGE_PACK_IDS = ['720', '632', '581', '578', '577'] as const

export type BatchBadgeAnchor = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
export type BatchTextBadgeShape = 'pill' | 'rounded' | 'square'

export interface BatchBadgeLayout {
  anchor: BatchBadgeAnchor
  /** Badge width as a percentage of the output width. */
  size: number
  /** Distance from the selected edges as a percentage of each output axis. */
  padding: number
  /** 0..100. */
  opacity: number
}

export interface BatchGraphicBadgeConfig extends BatchBadgeLayout {
  type: 'graphic'
  itemId: string
  packId: string
  imageUrl: string
  previewUrl: string
}

export interface BatchUploadBadgeConfig extends BatchBadgeLayout {
  type: 'upload'
  assetId: string
}

export interface BatchTextBadgeConfig extends BatchBadgeLayout {
  type: 'text'
  text: string
  shape: BatchTextBadgeShape
  fillColor: string
  textColor: string
}

export type BatchTextBadgeDraft = Omit<BatchTextBadgeConfig, keyof BatchBadgeLayout>

export type BatchBadgeConfig =
  | BatchGraphicBadgeConfig
  | BatchUploadBadgeConfig
  | BatchTextBadgeConfig

export interface BatchBadgeAsset {
  id: string
  /** Full bytes — what export composites. Up to 2048px. */
  dataUrl: string
  /** Small preview of the same asset, for on-screen tiles. Absent on legacy
   *  preset assets, which only ever stored the full bytes. */
  previewUrl?: string
}

export const DEFAULT_BATCH_BADGE_LAYOUT: BatchBadgeLayout = {
  anchor: 'top-right',
  size: 24,
  padding: 4,
  opacity: 100,
}

export const DEFAULT_TEXT_BADGE: BatchTextBadgeDraft = {
  type: 'text',
  text: 'FREE SHIPPING',
  shape: 'pill',
  fillColor: '#FF0560',
  textColor: '#FFFFFF',
}

export function createBatchTextBadgeDraft(badge?: BatchBadgeConfig | null): BatchTextBadgeDraft {
  if (badge?.type !== 'text') return { ...DEFAULT_TEXT_BADGE, text: '' }
  return {
    type: 'text',
    text: badge.text,
    shape: badge.shape,
    fillColor: badge.fillColor,
    textColor: badge.textColor,
  }
}

export function patchBatchTextBadgeDraft(
  draft: BatchTextBadgeDraft,
  patch: Partial<Omit<BatchTextBadgeDraft, 'type'>>,
  hasActiveTextBadge: boolean,
): BatchTextBadgeDraft {
  const next = { ...draft, ...patch }
  if (hasActiveTextBadge || next.text.trim()) return next
  return { ...next, text: DEFAULT_TEXT_BADGE.text }
}

export function restoreEmptyBatchTextBadgeDraft(draft: BatchTextBadgeDraft): BatchTextBadgeDraft {
  if (draft.text.trim()) return draft
  return { ...draft, text: DEFAULT_TEXT_BADGE.text }
}

export function withDefaultBadgeLayout<T extends object>(source: T): T & BatchBadgeLayout {
  return { ...source, ...DEFAULT_BATCH_BADGE_LAYOUT }
}

export function badgeLayoutsEqual(a: BatchBadgeConfig | null, b: BatchBadgeConfig | null): boolean {
  if (!a || !b) return a === b
  return (
    a.anchor === b.anchor &&
    a.size === b.size &&
    a.padding === b.padding &&
    a.opacity === b.opacity
  )
}

export function batchBadgesEqual(a: BatchBadgeConfig | null, b: BatchBadgeConfig | null): boolean {
  if (!badgeLayoutsEqual(a, b) || !a || !b || a.type !== b.type) return a === b
  if (a.type === 'graphic' && b.type === 'graphic') {
    return a.itemId === b.itemId && a.packId === b.packId && a.imageUrl === b.imageUrl
  }
  if (a.type === 'upload' && b.type === 'upload') return a.assetId === b.assetId
  if (a.type === 'text' && b.type === 'text') {
    return (
      a.text === b.text &&
      a.shape === b.shape &&
      a.fillColor === b.fillColor &&
      a.textColor === b.textColor
    )
  }
  return false
}

function roundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  const r = Math.max(0, Math.min(radius, width / 2, height / 2))
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + width - r, y)
  ctx.quadraticCurveTo(x + width, y, x + width, y + r)
  ctx.lineTo(x + width, y + height - r)
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height)
  ctx.lineTo(x + r, y + height)
  ctx.quadraticCurveTo(x, y + height, x, y + height - r)
  ctx.lineTo(x, y + r)
  ctx.quadraticCurveTo(x, y, x + r, y)
  ctx.closePath()
}

function anchoredPosition(
  anchor: BatchBadgeAnchor,
  canvasWidth: number,
  canvasHeight: number,
  badgeWidth: number,
  badgeHeight: number,
  paddingPercent: number,
): { x: number; y: number } {
  const padX = (canvasWidth * paddingPercent) / 100
  const padY = (canvasHeight * paddingPercent) / 100
  return {
    x: anchor.endsWith('left') ? padX : canvasWidth - padX - badgeWidth,
    y: anchor.startsWith('top') ? padY : canvasHeight - padY - badgeHeight,
  }
}

export interface BatchTextBadgeGeometry {
  badgeWidth: number
  badgeHeight: number
  fontSize: number
  horizontalInset: number
  verticalInset: number
}

export function calculateTextBadgeCornerRadius(
  shape: BatchTextBadgeShape,
  badgeHeight: number,
): number {
  if (shape === 'pill') return badgeHeight / 2
  if (shape === 'rounded') return badgeHeight * 0.18
  return 0
}

/** Shared preview/export geometry so long labels fit identically on both surfaces. */
export function calculateTextBadgeGeometry(
  width: number,
  height: number,
  badge: BatchTextBadgeConfig,
  measureText: (text: string, fontSize: number) => number,
): BatchTextBadgeGeometry {
  const maxWidth = Math.max(1, width * (1 - (badge.padding * 2) / 100))
  const maxHeight = Math.max(1, height * (1 - (badge.padding * 2) / 100))
  let badgeWidth = Math.min((width * badge.size) / 100, maxWidth)
  let horizontalInset = badgeWidth * 0.1
  let fontSize = badgeWidth * 0.2
  const measured = Math.max(1, measureText(badge.text, fontSize))
  fontSize = Math.min(fontSize, ((badgeWidth - horizontalInset * 2) / measured) * fontSize)
  let verticalInset = fontSize * 0.42
  let badgeHeight = fontSize + verticalInset * 2

  if (badgeHeight > maxHeight) {
    const scale = maxHeight / badgeHeight
    badgeWidth *= scale
    horizontalInset *= scale
    fontSize *= scale
    verticalInset *= scale
    badgeHeight *= scale
  }

  return { badgeWidth, badgeHeight, fontSize, horizontalInset, verticalInset }
}

async function ensureBadgeFont(): Promise<void> {
  if (typeof document === 'undefined' || !document.fonts?.load) return
  await document.fonts.load('700 32px New-Hero')
}

async function drawTextBadge(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  badge: BatchTextBadgeConfig,
): Promise<void> {
  await ensureBadgeFont()
  const { badgeWidth, badgeHeight, fontSize } = calculateTextBadgeGeometry(
    width,
    height,
    badge,
    (text, measuredFontSize) => {
      ctx.font = `700 ${measuredFontSize}px New-Hero, sans-serif`
      return ctx.measureText(text).width
    },
  )

  const { x, y } = anchoredPosition(
    badge.anchor,
    width,
    height,
    badgeWidth,
    badgeHeight,
    badge.padding,
  )
  const radius = calculateTextBadgeCornerRadius(badge.shape, badgeHeight)
  roundedRectPath(ctx, x, y, badgeWidth, badgeHeight, radius)
  ctx.fillStyle = badge.fillColor
  ctx.fill()

  ctx.font = `700 ${fontSize}px New-Hero, sans-serif`
  ctx.fillStyle = badge.textColor
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(badge.text, x + badgeWidth / 2, y + badgeHeight / 2)
}

/** Decoded badge sources, keyed by their URL / data URL.
 *
 *  One export composites the same badge onto every selected image, and
 *  `downloadBatchResults` fans those out with a single `Promise.all` — without a
 *  cache, `loadImageFromURL` (which has none of its own) decodes the badge once
 *  per tile, up to 2048px for an upload. Kept deliberately small: each entry
 *  pins a decoded bitmap, so this trades a few MB for N-1 decodes.
 */
const BADGE_IMAGE_CACHE_LIMIT = 4
const badgeImageCache = new Map<string, Promise<HTMLImageElement | null>>()

function loadBadgeImage(source: string): Promise<HTMLImageElement | null> {
  const cached = badgeImageCache.get(source)
  if (cached) {
    // Re-insert so eviction below drops the least recently used entry.
    badgeImageCache.delete(source)
    badgeImageCache.set(source, cached)
    return cached
  }
  const pending = (loadImageFromURL(source) as unknown as Promise<HTMLImageElement | null>).catch(
    (error) => {
      // A rejection must not be cached as this source's permanent answer.
      if (badgeImageCache.get(source) === pending) badgeImageCache.delete(source)
      throw error
    },
  )
  badgeImageCache.set(source, pending)
  while (badgeImageCache.size > BADGE_IMAGE_CACHE_LIMIT) {
    const oldest = badgeImageCache.keys().next().value
    if (oldest === undefined) break
    badgeImageCache.delete(oldest)
  }
  return pending
}

/** Drops every decoded badge. Exists for tests and for anything that needs the
 *  bytes re-fetched rather than served from the cache above. */
export function clearBadgeImageCache(): void {
  badgeImageCache.clear()
}

async function drawImageBadge(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  badge: BatchGraphicBadgeConfig | BatchUploadBadgeConfig,
  asset?: BatchBadgeAsset,
): Promise<void> {
  const source = badge.type === 'graphic' ? badge.imageUrl : asset?.dataUrl
  if (!source) throw new Error(`Missing uploaded badge asset: ${badge.type === 'upload' ? badge.assetId : ''}`)
  const image = await loadBadgeImage(source)
  if (!image?.width || !image?.height) throw new Error('Badge asset failed to load')

  const maxWidth = Math.max(1, width * (1 - (badge.padding * 2) / 100))
  const maxHeight = Math.max(1, height * (1 - (badge.padding * 2) / 100))
  const desiredWidth = Math.min((width * badge.size) / 100, maxWidth)
  const scale = Math.min(desiredWidth / image.width, maxHeight / image.height)
  const badgeWidth = image.width * scale
  const badgeHeight = image.height * scale
  const { x, y } = anchoredPosition(
    badge.anchor,
    width,
    height,
    badgeWidth,
    badgeHeight,
    badge.padding,
  )
  ctx.drawImage(image as unknown as CanvasImageSource, x, y, badgeWidth, badgeHeight)
}

/** Draws the badge after the product image so image adjustments/shadows never affect it. */
export async function drawBatchBadge(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  badge?: BatchBadgeConfig | null,
  asset?: BatchBadgeAsset,
): Promise<void> {
  if (!badge) return
  ctx.save()
  try {
    ctx.filter = 'none'
    ctx.globalAlpha = badge.opacity / 100
    if (badge.type === 'text') await drawTextBadge(ctx, width, height, badge)
    else await drawImageBadge(ctx, width, height, badge, asset)
  } finally {
    ctx.restore()
  }
}
